/**
 * Live market data for the marketing landing page — Binance only (via CoinMBX API).
 * Requires backend MARKET_DATA_SOURCE=binance.
 */
import { api } from "./api";

const POLL_INTERVAL_MS = 20000;

const ID_TO_SYMBOL = {
  bitcoin: "BTC",
  ethereum: "ETH",
  solana: "SOL",
  tether: "USDT",
  ripple: "XRP",
  cardano: "ADA",
  polkadot: "DOT",
  litecoin: "LTC",
  binancecoin: "BNB",
};

const state = new Map();
const listeners = new Set();

let pollTimer = null;
let refCount = 0;
let inFlight = null;

function notify() {
  listeners.forEach((fn) => fn(state));
}

function setFromRow(id, row) {
  const price = Number(row.price);
  const changePct = Number(row.change_pct_24h);
  if (!Number.isFinite(price)) return;
  state.set(id, {
    price,
    changePct: Number.isFinite(changePct) ? changePct : 0,
    up: (Number.isFinite(changePct) ? changePct : 0) >= 0,
    sparkline: [],
  });
}

async function fetchSparklineForSymbol(sym) {
  if (sym === "USDT" || sym === "USDC") return null;
  try {
    const { data } = await api.get(`/api/spot/sparkline/${sym}/USDT`, {
      params: { interval: "1h", limit: 24 },
    });
    const closes = (data?.closes ?? []).filter(Number.isFinite);
    return closes.length >= 2 ? closes : null;
  } catch {
    return null;
  }
}

async function fetchFromBackend() {
  const { data } = await api.get("/api/markets/list", {
    params: { quote: "USDT", category: "all", page_size: 100 },
  });
  const rows = data?.rows ?? [];
  const bySym = new Map(rows.map((r) => [String(r.symbol || "").toUpperCase(), r]));
  for (const [id, sym] of Object.entries(ID_TO_SYMBOL)) {
    const row = bySym.get(sym);
    if (row) setFromRow(id, row);
  }
  await Promise.all(
    Object.entries(ID_TO_SYMBOL).map(async ([id, sym]) => {
      if (!state.has(id)) return;
      const kline = await fetchSparklineForSymbol(sym);
      if (kline) {
        const cur = state.get(id);
        state.set(id, { ...cur, sparkline: kline });
      }
    }),
  );
  if (state.size) notify();
}

async function fetchOnce() {
  if (inFlight) return inFlight;
  inFlight = fetchFromBackend().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

function startPolling() {
  if (pollTimer) return;
  fetchOnce();
  pollTimer = setInterval(fetchOnce, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

export function subscribe(listener) {
  listeners.add(listener);
  refCount += 1;
  listener(state);
  if (refCount === 1) startPolling();
  return () => {
    listeners.delete(listener);
    refCount -= 1;
    if (refCount <= 0) stopPolling();
  };
}
